/**
 * Laying the edge band (ROUND4 item 6). The band rings the map on the three CLOSED sides - north,
 * east and west - and is merged into one mesh, so the whole thing costs a single draw call and
 * nothing in the shadow pass. The SOUTH edge is left bare on purpose: that is the badlands the
 * ranger drops into and walks out through when he is stranded, and it must not look sealed.
 *
 * Nothing here may eat playable ground, so each piece is pushed out by its own measured reach
 * (`edges.ts` builds every crest with +z inward) plus a clear margin.
 */

import { Box3 } from 'three';
import type { Pcg32 } from '../../sim/rng';
import type { MissionDef } from '../../sim/types';
import { edgeCrest } from '../kit/edges';
import { MeshBuilder, place } from '../kit/MeshBuilder';
import type { Palette } from '../palette';
import { riverFlattening } from './river';

/** Clear ground between the playable rectangle and the nearest triangle of the band. */
export const EDGE_CLEAR = 0.7;
/** Spacing of the pieces along an edge; they are 3.4 m wide, so they overlap into a solid run. */
const EDGE_PITCH = 2.3;
/** How much further out a piece may be thrown, so the band has depth instead of being a fence. */
const EDGE_DEPTH = 2.8;
/** Baked variants, as the flora are (05 §5 rule 4). */
const VARIANTS = 8;
/** How far the run overshoots a corner, so the two edges meet without a gap. */
const OVERSHOOT = 6;
/**
 * Where a river leaves the map the ridge already fades out (`terrainFromDef.edgeRise`), so the band
 * steps aside too: a treeline growing in the water would read worse than the river mouth it blocks.
 */
const RIVER_GAP = 0.85;

interface Variant {
  mb: MeshBuilder;
  /** The piece's own reach toward the map, in its local frame. */
  reach: number;
}

/**
 * The band for one mission: deterministic in the mission seed, seated on the ground it stands on.
 * `groundY` is the world's own surface function, so the band climbs the ridge `terrainFromDef`
 * raises outside the map instead of hanging over it.
 */
export function mapEdgeBand(
  def: MissionDef, palette: Palette, rng: Pcg32, groundY: (x: number, z: number) => number,
): MeshBuilder {
  const variants: Variant[] = [];
  for (let i = 0; i < VARIANTS; i++) {
    const mb = edgeCrest(palette, rng);
    const box = mb.build().boundingBox ?? new Box3();
    variants.push({ mb, reach: Math.max(0, box.max.z) });
  }
  const out = new MeshBuilder();
  const lay = (yaw: number, at: (along: number, outward: number) => [number, number], from: number, to: number): void => {
    for (let t = from; t <= to; t += EDGE_PITCH) {
      const v = variants[rng.int(VARIANTS)];
      if (!v) continue;
      const scale = rng.range(0.85, 1.25);
      const outward = EDGE_CLEAR + v.reach * scale + rng.range(0, EDGE_DEPTH);
      const [x, z] = at(t + rng.range(-0.55, 0.55), outward);
      if (def.waterBarriers.length > 0 && riverFlattening(def, x, z) > RIVER_GAP) continue;
      out.merge(v.mb, place(x, groundY(x, z) - 0.05, z, yaw, scale));
    }
  };
  const half = Math.PI / 2;
  lay(0, (t, o) => [t, -o], -OVERSHOOT, def.sizeX + OVERSHOOT);
  lay(half, (t, o) => [-o, t], -OVERSHOOT, def.sizeZ);
  lay(-half, (t, o) => [def.sizeX + o, t], -OVERSHOOT, def.sizeZ);
  return out;
}
